import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";

import instance from "../../../api/instance";
import { useSyncGlobalPageLoader } from "../../../contexts/global-page-loader-context";

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100, 125, 150];

interface EventOption {
  provider_event_id: string;
  event_name: string;
}

interface CasinoResultRow {
  market_id?: string;
  provider_market_id: string;
  result: any;
}

const getWinnerText = (result: any): string => {
  if (!result) return "-";

  if (typeof result === "string") return result;
  if (typeof result?.resultName === "string" && result.resultName) return result.resultName;
  if (typeof result?.result === "string" && result.result) return result.result;

  if (Array.isArray(result?.runners)) {
    const winnerRunner = result.runners.find((runner: any) => runner?.status === "WINNER");
    if (winnerRunner?.name) return String(winnerRunner.name);
  }

  if (Array.isArray(result?.result)) {
    const winnerRunner = result.result
      .map((round: any) => (Array.isArray(round?.marketRunner) ? round.marketRunner : []))
      .flat()
      .find((runner: any) => runner?.status === "WINNER");
    if (winnerRunner?.name) return String(winnerRunner.name);
  }

  return "-";
};

const CasinoResultPage = () => {
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [selectedEventId, setSelectedEventId] = useState("");
  const [eventOptions, setEventOptions] = useState<EventOption[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [rows, setRows] = useState<CasinoResultRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useSyncGlobalPageLoader(loading);

  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const fetchUniqueEvents = async () => {
      setEventsLoading(true);
      try {
        const { data } = await instance.get("event/business-unique-events");
        const list: EventOption[] = Array.isArray(data?.data) ? data.data : [];
        setEventOptions(list);
      } catch {
        setEventOptions([]);
      } finally {
        setEventsLoading(false);
      }
    };

    fetchUniqueEvents();
  }, []);

  const fetchMarketResults = useCallback(
    async (page: number, size: number) => {
      if (!selectedEventId) {
        setRows([]);
        setTotalCount(0);
        return;
      }

      setLoading(true);
      try {
        const startDate = moment(date).startOf("day").unix();
        const endDate = moment(date).endOf("day").unix();
        const offset = (page - 1) * size;

        const { data } = await instance.get("event/market-results", {
          params: {
            provider_event_id: selectedEventId,
            start_date: startDate,
            end_date: endDate,
            limit: size,
            offset,
          },
        });

        const list: CasinoResultRow[] = Array.isArray(data?.data?.rows) ? data.data.rows : [];
        setRows(list);
        setTotalCount(Number(data?.data?.count ?? 0));
      } catch {
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [date, selectedEventId]
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setHasLoaded(true);
    setCurrentPage(1);
    await fetchMarketResults(1, pageSize);
  };

  const filteredList = useMemo(() => {
    if (!searchText.trim()) return rows;
    const q = searchText.toLowerCase();
    return rows.filter((row) =>
      [row.provider_market_id, row.market_id, getWinnerText(row.result)].some((field) =>
        String(field ?? "").toLowerCase().includes(q)
      )
    );
  }, [rows, searchText]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pagedList = filteredList;
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <div className="live-bets-report listing-grid report-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Casino Result Report</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      <form method="post" onSubmit={handleSubmit}>
        <div className="row row5 mb-3">
          <div className="col-2 mb-2">
            <input
              type="date"
              className="form-control"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="col-2 mb-2">
            <select
              name="provider_event_id"
              className="form-control"
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
              disabled={eventsLoading}
            >
              <option value="">{eventsLoading ? "Loading events..." : "Select Event"}</option>
              {eventOptions.map((option) => (
                <option key={option.provider_event_id} value={option.provider_event_id}>
                  {option.event_name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 mb-2">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Loading..." : "Submit"}
            </button>
          </div>
        </div>
      </form>

      <div className="row">
        <div className="col-6">
          <div id="tickets-table_length" className="dataTables_length">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="d-inline-flex align-items-center">
              Show&nbsp;
              <select
                className="custom-select custom-select-sm"
                value={pageSize}
                onChange={(e) => {
                  const nextSize = Number(e.target.value);
                  setPageSize(nextSize);
                  setCurrentPage(1);
                  if (hasLoaded) {
                    fetchMarketResults(1, nextSize);
                  }
                }}
              >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              &nbsp;entries
            </label>
          </div>
        </div>
        <div className="col-6 text-right">
          <div id="tickets-table_filter" className="dataTables_filter text-md-right">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="d-inline-flex align-items-center">
              Search:
              <input
                type="search"
                placeholder="Search..."
                className="form-control ml-1 form-control"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </label>
          </div>
        </div>
      </div>

      <div className="table-responsive mb-0 report-table casino-result-table">
        <div className="table no-footer table-hover table-responsive-sm">
          <table
            id="eventsListTbl"
            role="table"
            aria-busy="false"
            aria-colcount={2}
            className="table b-table table-striped table-bordered b-table-fixed"
          >
            <thead>
              <tr role="row">
                <th role="columnheader" scope="col" aria-colindex={1} className="position-relative">
                  <div>Market Id</div>
                </th>
                <th role="columnheader" scope="col" aria-colindex={2} className="position-relative">
                  <div>Winner</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedList.length === 0 ? (
                <tr className="b-table-empty-row">
                  <td colSpan={2}>
                    <div role="alert" aria-live="polite">
                      <p className="text-center mb-0">{loading ? "Loading..." : "No data available in table"}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                pagedList.map((row, idx) => (
                  <tr key={`${row.provider_market_id}-${idx}`} role="row" aria-rowindex={idx + 1}>
                    <td aria-colindex={1}>
                      <button type="button" className="btn btn-link p-0 link-default">
                        {row.provider_market_id}
                      </button>
                    </td>
                    <td aria-colindex={2}>{getWinnerText(row.result)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="row pt-3">
        <div className="col">
          <div className="dataTables_paginate paging_simple_numbers float-right">
            <ul className="pagination pagination-rounded mb-0">
              <ul
                role="menubar"
                aria-disabled="false"
                aria-label="Pagination"
                className="pagination dataTables_paginate paging_simple_numbers my-0 b-pagination justify-content-end"
              >
                <li role="presentation" className={`page-item ${isFirstPage ? "disabled" : ""}`}>
                  <button
                    role="menuitem"
                    type="button"
                    aria-label="Go to first page"
                    disabled={isFirstPage}
                    className="page-link"
                    onClick={() => {
                      setCurrentPage(1);
                      if (hasLoaded) fetchMarketResults(1, pageSize);
                    }}
                  >
                    &laquo;
                  </button>
                </li>
                <li role="presentation" className={`page-item ${isFirstPage ? "disabled" : ""}`}>
                  <button
                    role="menuitem"
                    type="button"
                    aria-label="Go to previous page"
                    disabled={isFirstPage}
                    className="page-link"
                    onClick={() => {
                      const nextPage = Math.max(1, currentPage - 1);
                      setCurrentPage(nextPage);
                      if (hasLoaded) fetchMarketResults(nextPage, pageSize);
                    }}
                  >
                    &lsaquo;
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 10).map((page) => (
                  <li
                    key={page}
                    role="presentation"
                    className={`page-item ${currentPage === page ? "active" : ""}`}
                  >
                    <button
                      role="menuitemradio"
                      type="button"
                      aria-label={`Go to page ${page}`}
                      aria-checked={currentPage === page}
                      aria-posinset={page}
                      aria-setsize={totalPages}
                      className="page-link"
                      onClick={() => {
                        setCurrentPage(page);
                        if (hasLoaded) fetchMarketResults(page, pageSize);
                      }}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li role="presentation" className={`page-item ${isLastPage ? "disabled" : ""}`}>
                  <button
                    role="menuitem"
                    type="button"
                    aria-label="Go to next page"
                    disabled={isLastPage}
                    className="page-link"
                    onClick={() => {
                      const nextPage = Math.min(totalPages, currentPage + 1);
                      setCurrentPage(nextPage);
                      if (hasLoaded) fetchMarketResults(nextPage, pageSize);
                    }}
                  >
                    &rsaquo;
                  </button>
                </li>
                <li role="presentation" className={`page-item ${isLastPage ? "disabled" : ""}`}>
                  <button
                    role="menuitem"
                    type="button"
                    aria-label="Go to last page"
                    disabled={isLastPage}
                    className="page-link"
                    onClick={() => {
                      setCurrentPage(totalPages);
                      if (hasLoaded) fetchMarketResults(totalPages, pageSize);
                    }}
                  >
                    &raquo;
                  </button>
                </li>
              </ul>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CasinoResultPage;
